import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ScoreFixture } from "../../components/ScoreFixture.js";
import { Notice } from "../../components/ui.js";
import { isPlayable } from "../../lib/draw.js";
import type { TournamentEvent } from "../../lib/types.js";
import { EventGate, EventScreen, rosterOf, sideNames, useDraw } from "./shared.js";

/**
 * One match, one screen, one job.
 *
 * The pad used to open inside the draw, under a list of every other fixture — which meant
 * entering a score courtside was a scroll away from a Redraw button. It has its own URL now,
 * so the way back is the way back and nothing else is on screen.
 */
export function EventScore() {
  return <EventGate>{({ event }) => <Score event={event} />}</EventGate>;
}

function Score({ event }: { event: TournamentEvent }) {
  const { fixtureId } = useParams<{ fixtureId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: fixtures = [], isPending } = useDraw(event.id);
  const fixture = fixtures.find((entry) => entry.id === fixtureId);
  const roster = rosterOf(event);

  if (isPending) {
    return (
      <EventScreen event={event} title="Enter the score">
        <p className="micro">Loading…</p>
      </EventScreen>
    );
  }

  // Somebody else scored it while this was open, or the draw was replaced underneath it.
  // Both are ordinary on a busy night, and neither is worth a dead end.
  if (!fixture || !isPlayable(fixture)) {
    return (
      <EventScreen event={event} title="Enter the score">
        <Notice tone="attention">
          {fixture?.result
            ? "That match already has a score. An organiser can void it if it needs entering again."
            : "That match isn't ready to be scored — it may have been redrawn."}
        </Notice>
      </EventScreen>
    );
  }

  const back = () => navigate(`/events/${event.id}/draw`);

  return (
    <EventScreen event={event} title="Enter the score">
      <ScoreFixture
        fixtureId={fixture.id}
        sequence={fixture.sequence}
        round={fixture.round}
        nameA={sideNames(fixture.playersA, roster)}
        nameB={sideNames(fixture.playersB, roster)}
        onCancel={back}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ["draw", event.id] });
          void queryClient.invalidateQueries({ queryKey: ["event-results", event.id] });
          void queryClient.invalidateQueries({ queryKey: ["my-matches"] });
          // The community log has a new row in it, and the person who just entered the score
          // is often the next one to look at it.
          void queryClient.invalidateQueries({ queryKey: ["match-feed"] });
          navigate(`/events/${event.id}/results`);
        }}
      />
    </EventScreen>
  );
}
